// WHEP / WebRTC SDP munger。
//
// 背景: WebRTC の Opus は **デフォルトで mono** にネゴシエートされる。
//   - `stereo=1` (fmtp): 「自分は stereo を decode できる」(= 受信側の宣言)
//   - `sprop-stereo=1` (fmtp): 「自分は stereo で送る」(= 送信側の宣言)
//
// 視聴ブラウザの WHEP offer に **これらのフラグが無いと**、たとえ OBS が
// stereo で配信していても、Cloudflare Stream の SFU はその capabilities を
// 信じて mono に downmix した opus を返してくる (= 観測された「全部 mono に
// 聞こえる」現象の根本原因)。answer 側でも防御的に同じ munging を掛ける
// (Cloudflare が応答 SDP で stereo フラグを落とすパターンに備える)。
//
// 仕様参考: RFC 7587 §6.1 (Opus fmtp parameters)。
//
// 実装方針:
//   - SDP テキストを行で分解
//   - `a=rtpmap:<pt> opus/48000/2` 行から Opus の payload type を抽出
//   - `a=fmtp:<pt> <params>` 行のうち、上で抽出した pt のものだけ
//     `stereo=1` / `sprop-stereo=1` を追記 (既存なら no-op)
//   - その他の行は無改変で通す

/**
 * SDP テキスト中の Opus 用 fmtp に `stereo=1;sprop-stereo=1` を反映する。
 * - 既存の `a=fmtp:` 行があれば追記 (元から付いていれば no-op)。
 * - **`a=fmtp:` 行が無い場合は対応 rtpmap の直後に挿入する** (= 上流が
 *   fmtp を出力しなくなった場合の silently-mono 回帰を防ぐ)。
 * Opus が無い SDP はそのまま返す。
 */
export function enableOpusStereo(sdp: string): string {
  // Opus の payload type を rtpmap から抽出。`opus/48000/2` (= 2ch DSP rate)
  // は規格上一意 (mono 専用の rtpmap は無い) なので、これで全 opus PT を拾える。
  const opusPts = new Set<string>();
  for (const m of sdp.matchAll(/^a=rtpmap:(\d+)\s+opus\/48000\/2/gim)) {
    const pt = m[1];
    if (pt) opusPts.add(pt);
  }
  if (opusPts.size === 0) return sdp;

  // 既に `a=fmtp:` 行を持つ opus PT を列挙 → 残りは挿入対象。
  const ptsWithFmtp = new Set<string>();
  for (const m of sdp.matchAll(/^a=fmtp:(\d+)\s+/gim)) {
    const pt = m[1];
    if (pt && opusPts.has(pt)) ptsWithFmtp.add(pt);
  }
  const ptsToInsert = new Set<string>();
  for (const pt of opusPts) {
    if (!ptsWithFmtp.has(pt)) ptsToInsert.add(pt);
  }

  // CRLF / LF を保ったまま行ごとに処理して再結合。SDP は CRLF が公式だが、
  // Chrome の生成 SDP は LF のことも、Safari は CRLF のことも観測される。
  // 元の改行コードを保存するため、`split(/\r?\n/)` ではなく lookahead で残す。
  // split(/(\r?\n)/) は [content, sep, content, sep, ...] の交互配列を返す。
  const tokens = sdp.split(/(\r?\n)/);
  // 挿入用 separator を SDP 全体から推定 (= 先に出てくる方を採用)。
  const defaultSep = sdp.includes("\r\n") ? "\r\n" : "\n";

  for (let i = 0; i < tokens.length; i++) {
    const line = tokens[i];
    if (!line) continue;

    // 1) 既存 `a=fmtp:` 行を更新 (= stereo=1;sprop-stereo=1 を追記)
    const fm = line.match(/^a=fmtp:(\d+)\s+(.+)$/);
    if (fm) {
      const pt = fm[1];
      const params = fm[2];
      if (pt && params && opusPts.has(pt)) {
        let updated = params;
        // 既存 `stereo=1` 判定は negative lookbehind で `sprop-stereo=1` の suffix を除外。
        // 旧 regex `/\bstereo=1\b/` は `sprop-stereo=1` 内の `stereo=1` にもマッチし、
        // 「sprop-stereo=1 だけある SDP で standalone な stereo=1 が追記されない」
        // 回帰を起こしていた (= 視聴側デコーダが mono fallback)。
        if (!/(?<![-\w])stereo=1\b/.test(updated)) updated += ";stereo=1";
        if (!/\bsprop-stereo=1\b/.test(updated)) updated += ";sprop-stereo=1";
        tokens[i] = `a=fmtp:${pt} ${updated}`;
      }
      continue;
    }

    // 2) `a=fmtp:` が存在しない opus PT は rtpmap の直後に新規挿入
    const rm = line.match(/^a=rtpmap:(\d+)\s+opus\/48000\/2/i);
    if (rm) {
      const pt = rm[1];
      if (pt && ptsToInsert.has(pt)) {
        const existingSep = tokens[i + 1];
        if (existingSep === undefined) {
          // rtpmap が SDP の **最後の行** で末尾改行が無いケース。
          // splice(i+2, 0, fmtp, sep) で push しても rtpmap と fmtp の間に
          // separator が入らず malformed SDP になる (= setRemoteDescription が reject)。
          // そこで `sep + fmtp + sep` の 3 つを末尾に append する。
          // 末尾の sep は RFC 4566 準拠 (SDP は CRLF 終端) のために残す。
          tokens.push(defaultSep, `a=fmtp:${pt} stereo=1;sprop-stereo=1`, defaultSep);
          ptsToInsert.delete(pt);
          // 既に末尾を超えたので loop を抜けて join に進む。
          break;
        }
        // 通常ケース: rtpmap の後に separator がある。
        // tokens 構造: ... [i: rtpmap] [i+1: 既存 sep] [i+2: 次の line] ...
        // 挿入後:      ... [i: rtpmap] [i+1: 既存 sep] [i+2: 新 fmtp] [i+3: 新 sep (= 既存と同じ)] [i+4: 次の line] ...
        tokens.splice(i + 2, 0, `a=fmtp:${pt} stereo=1;sprop-stereo=1`, existingSep);
        ptsToInsert.delete(pt);
        // 挿入した 2 要素 (= 新 fmtp + 新 sep) を skip。次 iter で次の original line を見る。
        i += 2;
      }
    }
  }

  return tokens.join("");
}
